<template>
  <portal-layout>
    <div v-loading="loading" class="problem-detail-page">
      <template v-if="problem">
        <!-- 题目标题区 -->
        <div class="detail-header card">
          <div class="title-row">
            <h2 class="problem-title">
              <span class="problem-sn">{{ problem.problemId }}</span>
              {{ problem.title }}
            </h2>
            <el-tag :type="difficultyTagType(problem.difficulty)" size="small">
              {{ difficultyText(problem.difficulty) }}
            </el-tag>
          </div>
          <div class="meta-row">
            <span><i class="el-icon-time"></i> 时间限制：{{ problem.timeLimit }} ms</span>
            <span><i class="el-icon-cpu"></i> 内存限制：{{ problem.memoryLimit }} MB</span>
          </div>
        </div>

        <!-- 左侧题面 + 右侧代码 -->
        <div class="detail-body">
          <div class="problem-panel card">
            <h3 class="section-title">题目描述</h3>
            <markdown-view :content="problem.description"></markdown-view>

            <template v-if="problem.input">
              <h3 class="section-title">输入描述</h3>
              <markdown-view :content="problem.input"></markdown-view>
            </template>

            <template v-if="problem.output">
              <h3 class="section-title">输出描述</h3>
              <markdown-view :content="problem.output"></markdown-view>
            </template>

            <template v-if="examples && examples.length > 0">
              <h3 class="section-title">样例</h3>
              <div v-for="(example, index) in examples" :key="index" class="example-block">
                <div class="example-col">
                  <div class="example-label">输入 #{{ index + 1 }}</div>
                  <pre class="example-content">{{ example.input }}</pre>
                </div>
                <div class="example-col">
                  <div class="example-label">输出 #{{ index + 1 }}</div>
                  <pre class="example-content">{{ example.output }}</pre>
                </div>
              </div>
            </template>

            <template v-if="problem.hint">
              <h3 class="section-title">提示</h3>
              <markdown-view :content="problem.hint"></markdown-view>
            </template>
          </div>

          <div class="code-panel card">
            <div class="code-toolbar">
              <el-select v-model="language" size="small" class="lang-select" @change="onLanguageChange">
                <el-option v-for="lang in languages" :key="lang.id"
                           :label="lang.name" :value="lang.name"></el-option>
              </el-select>
              <div class="toolbar-btns">
                <el-button size="small" @click="openTestDrawer">在线自测</el-button>
                <el-button size="small" type="primary" :loading="submitting" @click="handleSubmit">提交评测</el-button>
              </div>
            </div>
            <code-editor v-if="editorReady" ref="editor" v-model="code"
                         :content-type="contentType" height="500px"></code-editor>
          </div>
        </div>

        <!-- 提交结果 -->
        <div v-if="submitResult" class="card result-card">
          <div class="result-head">
            <span class="result-label">评测结果</span>
            <span :class="['result-status', statusClass(submitResult.status)]">
              {{ statusText(submitResult.status) }}
            </span>
            <span v-if="isOi" class="result-score">得分 <b>{{ submitResult.score || 0 }}</b> / 100</span>
            <span v-if="submitResult.time" class="result-meta">耗时 {{ submitResult.time }} ms</span>
            <span v-if="submitResult.memory" class="result-meta">内存 {{ submitResult.memory }} KB</span>
          </div>
          <pre v-if="submitResult.errorMessage" class="result-message">{{ submitResult.errorMessage }}</pre>
        </div>

        <!-- 我的提交列表 -->
        <div class="card judge-list-card">
          <h3 class="section-title">我的提交</h3>
          <el-table v-loading="judgeLoading" :data="judges" stripe size="small" style="width: 100%">
            <el-table-column prop="submitId" label="提交ID" width="100"></el-table-column>
            <el-table-column label="状态" width="110">
              <template slot-scope="scope">
                <span :class="['status-text', statusClass(scope.row.status)]">
                  {{ scope.row.statusName || statusText(scope.row.status) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="language" label="语言" width="110"></el-table-column>
            <el-table-column v-if="isOi" label="得分" width="80">
              <template slot-scope="scope">{{ scope.row.score || 0 }}</template>
            </el-table-column>
            <el-table-column label="耗时" width="100">
              <template slot-scope="scope">{{ scope.row.time }} ms</template>
            </el-table-column>
            <el-table-column label="内存" width="110">
              <template slot-scope="scope">{{ scope.row.memory }} KB</template>
            </el-table-column>
            <el-table-column prop="submitTime" label="提交时间" min-width="170"></el-table-column>
            <el-table-column label="操作" width="90" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="viewJudge(scope.row)">查看</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="!judgeLoading && judges.length === 0" class="empty-tip">暂无提交记录</div>
        </div>
      </template>

      <!-- 在线自测抽屉 -->
      <el-drawer title="在线自测" :visible.sync="testDrawerVisible" size="560px"
                 :wrapper-closable="false">
        <div class="test-drawer-body">
          <el-tabs v-model="testTab">
            <el-tab-pane label="测试输入" name="input">
              <div class="test-field">
                <div class="test-label">自定义输入</div>
                <el-input v-model="testInput" type="textarea" :rows="8"
                          placeholder="请输入测试数据"></el-input>
              </div>
              <div class="test-field">
                <div class="test-label">
                  期望输出
                  <span class="test-label-tip">（可留空，留空时只要程序正常运行即视为通过）</span>
                </div>
                <el-input v-model="testOutput" type="textarea" :rows="6"
                          placeholder="请输入期望输出"></el-input>
              </div>
            </el-tab-pane>
            <el-tab-pane label="运行结果" name="result">
              <div v-if="testResult" class="test-result">
                <div class="result-head">
                  <span :class="['result-status', statusClass(testResult.status)]">
                    {{ testResult.statusName || statusText(testResult.status) }}
                  </span>
                  <span v-if="testResult.time" class="result-meta">耗时 {{ testResult.time }} ms</span>
                </div>
                <div class="test-label">程序输出</div>
                <pre class="test-output">{{ testResult.stdout || '（无输出）' }}</pre>
                <div v-if="testResult.errorMessage" class="test-label">错误信息</div>
                <pre v-if="testResult.errorMessage" class="test-output error">{{ testResult.errorMessage }}</pre>
              </div>
              <div v-else class="empty-tip">尚未运行</div>
            </el-tab-pane>
          </el-tabs>
          <div class="test-drawer-footer">
            <el-button size="small" @click="testDrawerVisible = false">关闭</el-button>
            <el-button size="small" type="primary" :loading="testing" @click="handleTest">运行</el-button>
          </div>
        </div>
      </el-drawer>
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import CodeEditor from '@/components/CodeEditor.vue';
import MarkdownView from '@/components/MarkdownView.vue';
import api from '@/common/api';
import storage from '@/common/storage.js';

const STATUS_TEXT = {
  '-10': '未提交', '-5': '未知', '-4': '已取消', '-3': '格式错误', '-2': '编译错误',
  '-1': '答案错误', '0': '通过', '1': '超出时间限制', '2': '超出内存限制', '3': '运行时错误',
  '4': '系统错误', '5': '等待中', '6': '编译中', '7': '判题中', '8': '部分通过', '9': '提交中', '10': '提交失败'
};

export default {
  name: 'PortalProblemDetail',
  components: { PortalLayout, CodeEditor, MarkdownView },
  data() {
    return {
      loading: false,
      problem: null,
      examples: [],
      languages: [],
      language: '',
      contentType: 'cpp',
      codeTemplateMap: {},
      code: '',
      editorReady: false,
      submitting: false,
      submitResult: null,
      judges: [],
      judgeLoading: false,
      testDrawerVisible: false,
      testTab: 'input',
      testInput: '',
      testOutput: '',
      testResult: null,
      testing: false
    }
  },
  computed: {
    // OI 题（type=1）才有得分概念：ACM 题只判通过/未通过，展示分数会被误读成部分分
    isOi() {
      return Number(this.problem && this.problem.type) === 1;
    }
  },
  created() {
    if (!this.checkLogin()) {
      return;
    }
    this.pid = this.$route.query.id;
    if (!this.pid) {
      this.$message.error('缺少题目参数');
      return;
    }
    this.queryDetail();
  },
  methods: {
    checkLogin() {
      if (!storage.getString('portalMember')) {
        this.$router.push({ name: 'PortalLogin', query: { redirect: this.$route.fullPath } }).catch(() => {});
        return false;
      }
      return true;
    },
    queryDetail() {
      this.loading = true;
      api.hoj_problem_detail({ id: this.pid }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.problem = r.data.data;
          this.examples = r.data.data.examples || [];
          //题目级代码模板（hoj_code_template）：按语言ID建映射，供初始代码取值
          this.codeTemplateMap = this.buildTemplateMap(r.data.data.codeTemplates);
        } else {
          this.$message.error(r.data.msg || '题目加载失败');
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.loading = false;
        this.queryLanguages();
        this.queryJudges();
      });
    },
    // 题目级模板按语言ID建映射（key 与语言列表的 id 同为语言ID）
    buildTemplateMap(codeTemplates) {
      const map = {};
      (codeTemplates || []).forEach((item) => {
        if (item && item.lid !== undefined && item.lid !== null) {
          map[item.lid] = item.code;
        }
      });
      return map;
    },
    // 初始代码取值：该题配置了该语言的模板就用题目的（配置为空则展示空编辑器），
    // 没有配置才回退到语言的默认模板
    resolveTemplate(lang) {
      if (!lang) {
        return '';
      }
      const map = this.codeTemplateMap || {};
      if (Object.prototype.hasOwnProperty.call(map, lang.id)) {
        const tpl = map[lang.id];
        return tpl === undefined || tpl === null ? '' : tpl;
      }
      return lang.codeTemplate || '';
    },
    queryLanguages() {
      api.hoj_problem_language_list({ id: this.pid }).then((r) => {
        if (r.data.code === "200") {
          this.languages = r.data.rows || r.data.data || [];
          if (this.languages.length > 0) {
            this.language = this.languages[0].name;
            this.contentType = this.languages[0].contentType || 'cpp';
            this.code = this.resolveTemplate(this.languages[0]);
          }
          this.editorReady = true;
        }
      });
    },
    queryJudges() {
      this.judgeLoading = true;
      api.hoj_problem_judge_list({ pid: Number(this.pid), onlyMine: true, pageNum: 1, pageSize: 20 })
        .then((r) => {
          if (r.data.code === "200") {
            this.judges = r.data.rows || r.data.data || [];
          }
        }).catch(() => {}).finally(() => {
          this.judgeLoading = false;
        });
    },
    onLanguageChange(name) {
      const lang = this.languages.find((item) => item.name === name);
      if (lang) {
        this.contentType = lang.contentType || 'cpp';
        this.code = this.resolveTemplate(lang);
        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.setValue(this.code);
          }
        });
      }
    },
    handleSubmit() {
      if (!this.language) {
        this.$message.error('请选择语言');
        return;
      }
      if (!this.code || !this.code.trim()) {
        this.$message.error('请填写代码');
        return;
      }
      this.submitting = true;
      this.submitResult = null;
      api.hoj_problem_submit({
        pid: Number(this.pid),
        code: this.code,
        language: this.language
      }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.submitResult = r.data.data;
          this.queryJudges();
        } else {
          this.$message.error(r.data.msg || '提交失败');
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.submitting = false;
      });
    },
    openTestDrawer() {
      this.testDrawerVisible = true;
      this.testTab = 'input';
      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.refresh();
        }
      });
    },
    handleTest() {
      if (!this.language) {
        this.$message.error('请选择语言');
        return;
      }
      if (!this.code || !this.code.trim()) {
        this.$message.error('请填写代码');
        return;
      }
      this.testing = true;
      api.hoj_problem_test({
        pid: Number(this.pid),
        code: this.code,
        language: this.language,
        testInput: this.testInput,
        testOutput: this.testOutput
      }).then((r) => {
        if (r.data.code === "200") {
          this.testResult = r.data.data;
          this.testTab = 'result';
        } else {
          this.$message.error(r.data.msg || '运行失败');
        }
      }).catch(() => {}).finally(() => {
        this.testing = false;
      });
    },
    viewJudge(row) {
      this.$router.push('/portal/problem/judge?id=' + row.submitId).catch(() => {});
    },
    difficultyText(difficulty) {
      const map = { 0: '简单', 1: '中等', 2: '困难' };
      return map[difficulty] || '未知';
    },
    difficultyTagType(difficulty) {
      const map = { 0: 'success', 1: 'warning', 2: 'danger' };
      return map[difficulty] || 'info';
    },
    statusText(status) {
      return STATUS_TEXT[String(status)] || '未知';
    },
    statusClass(status) {
      const s = String(status);
      if (s === '0') return 'status-ac';
      if (s === '-1' || s === '-2' || s === '-3' || s === '1' || s === '2' || s === '3' || s === '4') return 'status-error';
      return 'status-other';
    }
  }
};
</script>
<style scoped>
.card {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.detail-header {
  padding-bottom: 16px;
}
.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.problem-title {
  font-size: 20px;
  color: #303133;
  margin: 0;
}
.problem-sn {
  color: #909399;
  font-weight: normal;
  margin-right: 6px;
}
.meta-row {
  display: flex;
  gap: 30px;
  font-size: 13px;
  color: #909399;
}
.detail-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.problem-panel {
  flex: 1;
  min-width: 0;
  max-height: 760px;
  overflow-y: auto;
}
.code-panel {
  flex: 1;
  min-width: 0;
}
.section-title {
  font-size: 16px;
  color: #303133;
  margin: 0 0 12px 0;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 1.2;
}
.problem-panel .section-title:not(:first-child) {
  margin-top: 24px;
}
.example-block {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.example-col {
  flex: 1;
  min-width: 0;
}
.example-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}
.example-content {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0;
  font-size: 13px;
  font-family: Consolas, Monaco, monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 220px;
  overflow-y: auto;
  color: #303133;
}
.code-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.lang-select {
  width: 150px;
}
.result-card .result-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}
.result-label {
  font-size: 15px;
  color: #303133;
}
.result-status {
  font-size: 16px;
  font-weight: 600;
}
.result-score {
  font-size: 14px;
  color: #E6A23C;
}
.result-score b {
  font-size: 18px;
}
.result-meta {
  font-size: 13px;
  color: #909399;
}
.result-message {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 12px;
  margin: 0;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
  color: #606266;
}
.status-ac {
  color: #67C23A;
}
.status-error {
  color: #F56C6C;
}
.status-other {
  color: #E6A23C;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}
.test-drawer-body {
  padding: 0 20px 20px 20px;
  height: calc(100% - 20px);
  display: flex;
  flex-direction: column;
}
.test-field {
  margin-bottom: 16px;
}
.test-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}
.test-label-tip {
  color: #909399;
  font-size: 12px;
}
.test-drawer-footer {
  margin-top: auto;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}
.test-output {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 0 16px 0;
  font-size: 13px;
  font-family: Consolas, Monaco, monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
  color: #303133;
}
.test-output.error {
  color: #F56C6C;
}
</style>
